// src/services/crecheApi.js
import axiosInstance from './axiosInstance';
import axios from 'axios';

// Instância axios pública (sem autenticação) para rotas públicas
const publicAxios = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api'
});

const crecheApi = {
  // 📌 Grupos
  async listarGrupos(params = {}) {
    const { data } = await axiosInstance.get('/creche/grupos', { params });
    return data;
  },

  // 📌 Matrículas
  async criarMatricula(body) {
    const { data } = await axiosInstance.post('/creche/matriculas', body);
    return data;
  },

  async obterMatricula(id) {
    const { data } = await axiosInstance.get(`/creche/matriculas/${id}`);
    return data;
  },

  async listarMatriculas(params = {}) {
    const { data } = await axiosInstance.get('/creche/matriculas', { params });
    return data;
  },

  async atualizarMatricula(id, body) {
    const { data } = await axiosInstance.patch(`/creche/matriculas/${id}`, body);
    return data;
  },

  // 📌 Check-ins / Saídas
  async registarCheckin(body) {
    const { data } = await axiosInstance.post('/creche/checkins', body);
    return data;
  },

  async registarSaida(id, body = {}) {
    const { data } = await axiosInstance.patch(`/creche/checkins/${id}/saida`, body);
    return data;
  },

  // 📌 Presenças (Admin)
  async registarPresenca(body) {
    const { data } = await axiosInstance.post('/creche/presencas', body);
    return data;
  },

  async listarPresencas(params = {}) {
    const { data } = await axiosInstance.get('/creche/presencas', { params });
    return data;
  },

  // 📌 Responsáveis
  async criarResponsavel(body) {
    const { data } = await axiosInstance.post('/creche/responsaveis', body);
    return data;
  },

  async vincularCrianca(body) {
    const { data } = await axiosInstance.post('/creche/crianca-responsavel', body);
    return data;
  },

  async listarResponsaveis(params = {}) {
    const { data } = await axiosInstance.get('/creche/responsaveis', { params });
    return data;
  },

  // 📌 Ficha de Saúde (Admin)
  async guardarFichaSaude(body) {
    const { data } = await axiosInstance.post('/creche/saude', body);
    return data;
  },

  async obterFichaSaude(estudanteId) {
    const { data } = await axiosInstance.get(`/creche/saude/${estudanteId}`);
    return data;
  },

  // 📊 Relatórios
  async relatorioOcupacao() {
    const { data } = await axiosInstance.get('/creche/relatorios/ocupacao');
    return data;
  },

  async relatorioAssiduidade() {
    const { data } = await axiosInstance.get('/creche/relatorios/assiduidade');
    return data;
  },

  async relatorioInadimplencia() {
    const { data } = await axiosInstance.get('/creche/relatorios/inadimplencia');
    return data;
  },

  // 🧩 ==========================================================
  // 🧩 FUNÇÕES DO RESPONSÁVEL (FASE 1)
  // =============================================================

  async listarMinhasCriancas() {
    const { data } = await axiosInstance.get('/creche/responsavel/minhas-criancas');
    return data?.data || data;
  },

  async listarMeusCheckins(estudanteId, params = {}) {
    const { data } = await axiosInstance.get('/creche/responsavel/checkins', { 
      params: { estudanteId, ...params } 
    });
    return data?.data || data;
  },

  async listarMinhasPresencas(estudanteId, params = {}) {
    const { data } = await axiosInstance.get('/creche/responsavel/presencas', { 
      params: { estudanteId, ...params } 
    });
    return data?.data || data;
  },

  async obterMinhaFichaSaude(estudanteId) {
    const { data } = await axiosInstance.get('/creche/responsavel/saude', { 
      params: { estudanteId } 
    });
    return data?.data || data;
  },

  async listarMinhasPrestacoes(estudanteId) {
    const { data } = await axiosInstance.get('/creche/responsavel/prestacoes', { 
      params: { estudanteId } 
    });
    return data?.data || data;
  },

  // 🧩 Comunicados
  async listarComunicados() {
    const { data } = await axiosInstance.get('/creche/comunicados');
    return data?.data || data;
  },

  async marcarComunicadoComoLido(id) {
    const { data } = await axiosInstance.post(`/creche/comunicados/${id}/marcar-lido`);
    return data;
  },

  // 🆕 ==========================================================
  // 🆕 PRÉ-INSCRIÇÃO PÚBLICA (FASE 2) - Sem autenticação
  // =============================================================

  async criarPreInscricaoPublica(payload) {
    const { data } = await publicAxios.post('/creche-public/pre-inscricao', payload);
    return data;
  },

  async uploadDocumentosPreInscricao(preInscricaoId, arquivos) {
    const formData = new FormData();
    
    arquivos.forEach((arquivo) => {
      formData.append('documentos', arquivo);
    });

    const { data } = await publicAxios.post(
      `/creche-public/pre-inscricao/${preInscricaoId}/documentos`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return data;
  },

  async listarInstituicoes() {
    try {
      const { data } = await publicAxios.get('/creche-public/instituicoes');
      return data?.data || data || [];
    } catch (err) {
      console.error('Erro ao listar instituições:', err);
      return [];
    }
  },

  async listarGruposPublico(instituicaoId) {
    try {
      const { data } = await publicAxios.get(`/creche-public/instituicoes/${instituicaoId}/grupos`);
      return data?.data || data || [];
    } catch (err) {
      console.error('Erro ao listar grupos:', err);
      return [];
    }
  },

  // 🆕 ==========================================================
  // 🆕 GESTÃO DE PRÉ-INSCRIÇÕES (FASE 2) - Admin com autenticação
  // =============================================================

  async listarPreInscricoes() {
    const { data } = await axiosInstance.get('/creche/pre-inscricoes');
    return data?.data || data || [];
  },

  async detalharPreInscricao(id) {
    const { data } = await axiosInstance.get(`/creche/pre-inscricoes/${id}`);
    return data?.data || data;
  },

  async atualizarStatusPreInscricao(id, status, motivo = '') {
    const { data } = await axiosInstance.patch(
      `/creche/pre-inscricoes/${id}/status`,
      { status, motivo }
    );
    return data;
  },
};

export default crecheApi;